export interface Notification {
  id: string;
  text: string;
  date: string; // ISO 8601 string
  read: 'read' | 'unread';
  type: 'information' | 'warning' | null;
  notification_link: string;
}

export interface NotificationWithTime extends Notification {
  time: string; // Format: "HH:mm"
  title: string;
  description: string;
}

export interface GroupedNotifications {
  [key: string]: NotificationWithTime[];
}

interface GroupedSection {
  date: string;
  items: NotificationWithTime[];
}

export function groupNotificationsByDate(notifications: Notification[]): GroupedSection[] {
  const groups: Record<string, NotificationWithTime[]> = {};

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const formatDateKey = (date: Date): string => date.toISOString().split('T')[0];

  const formatTime = (date: Date): string =>
    date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  const todayKey = formatDateKey(today);
  const yesterdayKey = formatDateKey(yesterday);

  for (const item of notifications) {
    const itemDate = new Date(item.date);
    const itemKey = formatDateKey(itemDate);
    const [title = '', description = ''] = (item.text ?? '').split('/n');

    let groupKey: string;
    if (itemKey === todayKey) {
      groupKey = 'Today';
    } else if (itemKey === yesterdayKey) {
      groupKey = 'Yesterday';
    } else {
      groupKey = itemKey;
    }

    const itemWithTime: NotificationWithTime = {
      ...item,
      title,
      description,
      time: formatTime(itemDate),
    };

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(itemWithTime);
  }

  // Sort items within each group by time in descending order (latest first)
  Object.keys(groups).forEach(groupKey => {
    groups[groupKey].sort((a, b) => b.time.localeCompare(a.time));
  });

  // Convert to array and sort: Today first, Yesterday second, then by date descending
  const result: GroupedSection[] = [];

  if (groups['Today']) {
    result.push({ date: 'Bugün', items: groups['Today'] });
  }
  if (groups['Yesterday']) {
    result.push({ date: 'Dünən', items: groups['Yesterday'] });
  }

  const otherDates = Object.keys(groups)
    .filter(key => key !== 'Today' && key !== 'Yesterday')
    .sort((a, b) => b.localeCompare(a)); // Descending

  for (const date of otherDates) {
    result.push({ date, items: groups[date] });
  }

  return result;
}
